import { useSyncExternalStore } from 'react';
import { Link } from 'wouter';
import { useOfflineState } from '../offline/state.ts';
import { Icon } from './Icon.tsx';
import styles from './OfflineBanner.module.css';

// A technician on site loses signal in a basement or a plant room. Edits
// made there are queued rather than lost, so this banner says what the app
// is doing about them: that there is no connection, how much is waiting,
// and — the one thing that needs a person — how much cannot be sent until
// somebody chooses between their edit and what changed underneath it.

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function isOnline(): boolean {
  return navigator.onLine;
}

/** "3 changes" / "1 change": what is waiting, in words. */
export function describeChanges(count: number): string {
  return `${String(count)} ${count === 1 ? 'change' : 'changes'}`;
}

/** "waiting to sync", with the last sync when there was one. */
function waitingText(count: number, syncedAt: string | null): string {
  const when = syncedAt === null ? '' : `; last synced ${new Date(syncedAt).toLocaleString()}`;
  return `${describeChanges(count)} waiting to sync${when}`;
}

/** The same browser connection observation for banners and operations that cannot queue. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, isOnline);
}

export function OfflineBanner() {
  const online = useOnline();
  const { queued, failed, conflicts, syncedAt } = useOfflineState();
  const waiting = queued + failed;
  return (
    // Always in the document (empty until there is something to say) so the
    // announcement lands.
    <div aria-live="polite">
      {online ? null : (
        <p className={styles['banner']}>
          <Icon name="info" size="1rem" />
          You&rsquo;re offline. Changes are saved here and sent when you&rsquo;re back.
        </p>
      )}
      {waiting === 0 ? null : (
        <p className={styles['waiting']}>
          <Icon name="info" size="1rem" />
          <Link href="/offline">{waitingText(waiting, syncedAt)}</Link>
        </p>
      )}
      {conflicts === 0 ? null : (
        <p className={styles['banner']}>
          <Icon name="info" size="1rem" />
          <Link href="/offline">{describeChanges(conflicts)} conflict</Link>
        </p>
      )}
    </div>
  );
}
